import { api, ApiError } from '../../core/network/api';
import { esc } from './item-icon';
import { mountBackToLanding } from './back-to-landing';
import { getIntlLocale, t } from '../i18n/i18n';
import { legalContentHtml } from './legal-page';

/**
 * `/news/<slug>` — one published CMS news article (2026-10-21), the target of
 * the cards in the landing's News section. Public: read signed out, from
 * core's `GET /api/cms/news/:slug` (published only; 404 otherwise).
 *
 * Like legal pages, the admin writes these in a plain textarea, so content is
 * rendered as escaped paragraphs — never as markup.
 */
export interface NewsArticleData {
  slug: string;
  title: string;
  content: string;
  tag?: string;
  imageUrl?: string | null;
  publishedAt?: string | null;
  createdAt?: string;
}

export function newsSlugFromPath(pathname: string): string | null {
  const m = /^\/news\/([^/]+)\/?$/.exec(pathname);
  if (!m) return null;
  try {
    return decodeURIComponent(m[1]);
  } catch {
    return null;
  }
}

/** Only http(s) image URLs (absolute or same-origin relative) reach an `<img src>`. */
export function safeImageUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  try {
    const u = new URL(raw, location.href);
    return u.protocol === 'https:' || u.protocol === 'http:' ? u.href : null;
  } catch {
    return null;
  }
}

export function newsArticleHtml(a: NewsArticleData): string {
  const date = new Date(a.publishedAt ?? a.createdAt ?? '');
  const meta = [
    a.tag ? a.tag.toUpperCase() : '',
    Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString(getIntlLocale()),
  ].filter(Boolean).join(' · ');
  const img = safeImageUrl(a.imageUrl);
  return `
    ${img ? `<img src="${esc(img)}" alt="" class="w-full aspect-video object-cover rounded mb-6" />` : ''}
    ${meta ? `<p class="text-text-dim text-xs tracking-wider mb-2">${esc(meta)}</p>` : ''}
    <h1 class="font-heading text-gold text-2xl sm:text-3xl tracking-wide mb-6">${esc(a.title)}</h1>
    <div class="text-text text-[15px] leading-relaxed break-words">${legalContentHtml(a.content)}</div>`;
}

function shell(inner: string): string {
  return `
    <main class="bg-auth-gradient px-4 pb-12">
      <article id="news-page" class="mx-auto w-full max-w-[760px] bg-auth-card border border-border rounded-lg p-6 sm:p-10 shadow-lift">
        ${inner}
      </article>
    </main>`;
}

export function mountNewsPage(root: HTMLElement, slug: string): () => void {
  let alive = true;
  root.innerHTML = shell(`<p class="text-text-dim text-sm">${esc(t('Loading…'))}</p>`);
  const unmountBack = mountBackToLanding(root);

  void (async () => {
    let html: string;
    try {
      const article = await api.get<NewsArticleData>(`/cms/news/${encodeURIComponent(slug)}`);
      document.title = `${article.title} — Tallow`;
      html = newsArticleHtml(article);
    } catch (err) {
      const missing = err instanceof ApiError && err.status === 404;
      html = `
        <h1 class="font-heading text-gold text-2xl mb-3">${esc(missing ? t('Page not found') : t('Could not load this page'))}</h1>
        <p class="text-text-dim text-sm">${esc(missing
          ? t('This page does not exist or is no longer published.')
          : t('Please try again in a moment.'))}</p>`;
    }
    if (!alive) return;
    const el = root.querySelector('#news-page');
    if (el) el.innerHTML = html;
  })();

  return () => {
    alive = false;
    unmountBack();
  };
}
